import React, { createContext, useContext, useReducer, useEffect, useState } from 'react';
import { CartState, CartItem, Vehicle, Product } from '../types';
import { getApiUrl } from '../config/api';
import { useAuth } from './AuthContext';

interface PriceCalculation {
  subtotalExclVat: number;
  shippingCost: number;
  originalShippingCost: number;
  discount: {
    appliedTier: any;
    isFixedDiscount: boolean;
    userDiscountPercentage: number;
    shippingDiscountPercentage: number;
    tierDiscountPercentage: number;
    shippingDiscountAmount: number;
    tierDiscountAmount: number;
  };
  vat: {
    rate: number;
    onProducts: number;
    onShipping: number;
    total: number;
  };
  totals: {
    exclVat: number;
    inclVat: number;
  };
  shipping: {
    totalQuantity: number;
    colloCount: number;
    costPerCollo: number;
  };
  credit_limit: {
    credit_limit: number | null;
    credit_limit_source: string;
    outstanding_amount: number;
    available_credit: number | null;
    credit_status: string;
  };
}

interface CartContextType {
  state: CartState;
  priceCalculation: PriceCalculation | null;
  isCalculatingPrice: boolean;
  addToCart: (vehicle: Vehicle, quantity: number) => void;
  addProductToCart: (product: Product, quantity: number) => void;
  updateQuantity: (vehicleId: string, quantity: number) => void;
  removeFromCart: (vehicleId: string) => void;
  clearCart: () => void;
  calculatePrices: () => Promise<void>;
}

type CartAction =
  | { type: 'ADD_TO_CART'; payload: { vehicle: Vehicle; quantity: number } }
  | { type: 'UPDATE_QUANTITY'; payload: { vehicleId: string; quantity: number } }
  | { type: 'REMOVE_FROM_CART'; payload: { vehicleId: string } }
  | { type: 'CLEAR_CART' }
  | { type: 'LOAD_CART'; payload: CartState };

const CartContext = createContext<CartContextType | undefined>(undefined);

const TAX_RATE = 0.21; // 21% VAT

function cartReducer(state: CartState, action: CartAction): CartState {
  let newItems: CartItem[];

  switch (action.type) {
    case 'ADD_TO_CART': {
      const existingItemIndex = state.items.findIndex(
        item => item.vehicle.id === action.payload.vehicle.id
      );

      if (existingItemIndex >= 0) {
        newItems = state.items.map((item, index) =>
          index === existingItemIndex
            ? {
              ...item,
              quantity: item.quantity + action.payload.quantity,
              totalPrice: action.payload.vehicle.price * (item.quantity + action.payload.quantity)
            }
            : item
        );
      } else {
        newItems = [
          ...state.items,
          {
            vehicle: action.payload.vehicle,
            quantity: action.payload.quantity,
            totalPrice: action.payload.vehicle.price * action.payload.quantity
          }
        ];
      }
      break;
    }

    case 'UPDATE_QUANTITY': {
      if (action.payload.quantity === 0) {
        newItems = state.items.filter(item => item.vehicle.id !== action.payload.vehicleId);
      } else {
        newItems = state.items.map(item =>
          item.vehicle.id === action.payload.vehicleId
            ? {
              ...item,
              quantity: action.payload.quantity,
              totalPrice: item.vehicle.price * action.payload.quantity
            }
            : item
        );
      }
      break;
    }

    case 'REMOVE_FROM_CART': {
      newItems = state.items.filter(item => item.vehicle.id !== action.payload.vehicleId);
      break;
    }

    case 'CLEAR_CART': {
      newItems = [];
      break;
    }

    case 'LOAD_CART': {
      return action.payload;
    }

    default:
      return state;
  }

  const subtotal = newItems.reduce((sum, item) => sum + item.totalPrice, 0);
  const tax = subtotal * TAX_RATE;
  const total = subtotal + tax;

  return {
    items: newItems,
    subtotal,
    tax,
    total
  };
}

const initialState: CartState = {
  items: [],
  subtotal: 0,
  tax: 0,
  total: 0
};

export function CartProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(cartReducer, initialState);
  const [priceCalculation, setPriceCalculation] = useState<PriceCalculation | null>(null);
  const [isCalculatingPrice, setIsCalculatingPrice] = useState(false);
  const [isCartLoaded, setIsCartLoaded] = useState(false);
  const { isAuthenticated } = useAuth();

  // Load cart from localStorage on mount
  useEffect(() => {
    console.log('CartContext: Loading cart from localStorage...');
    const savedCart = localStorage.getItem('renaultPortalCart');
    console.log('CartContext: Saved cart from localStorage:', savedCart);

    if (savedCart) {
      try {
        const cartData = JSON.parse(savedCart);
        console.log('CartContext: Parsed cart data:', cartData);
        dispatch({ type: 'LOAD_CART', payload: cartData });
        console.log('CartContext: Cart loaded successfully');
      } catch (error) {
        console.error('Failed to load cart from localStorage:', error);
      }
    } else {
      console.log('CartContext: No saved cart found in localStorage');
    }

    setIsCartLoaded(true);
  }, []);

  // Save cart to localStorage whenever it changes (but only after initial load)
  useEffect(() => {
    if (!isCartLoaded) {
      console.log('CartContext: Skipping save - cart not loaded yet');
      return;
    }

    console.log('CartContext: Saving cart to localStorage:', state);
    localStorage.setItem('renaultPortalCart', JSON.stringify(state));
  }, [state, isCartLoaded]);

  // Calculate prices whenever cart items change (including empty cart for credit limit)
  // Only calculate if user is authenticated
  useEffect(() => {
    if (isAuthenticated) {
      calculatePrices();
    } else {
      // Clear price calculation when not authenticated
      setPriceCalculation(null);
    }
  }, [state.items, isAuthenticated]);

  const calculatePrices = async () => {
    // Skip calculation if user is not authenticated
    if (!isAuthenticated) {
      console.log('CartContext: Skipping price calculation - user not authenticated');
      setPriceCalculation(null);
      return;
    }

    setIsCalculatingPrice(true);

    try {
      const cartData = {
        items: state.items.map(item => ({
          id: item.vehicle.id,
          name: item.vehicle.name,
          price: item.vehicle.price,
          quantity: item.quantity
        }))
      };

      const response = await fetch(getApiUrl('/api/web/cart/calculate-price'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(cartData),
        credentials: 'include'
      });

      const result = await response.json();
      console.log('Price calculation response:', result);

      if (result.meta?.result === 'success') {
        console.log('Setting price calculation:', result.data.calculations);
        setPriceCalculation(result.data.calculations);
      } else if (result.status === 'success') {
        console.log('Setting price calculation (legacy):', result.data.calculations);
        setPriceCalculation(result.data.calculations);
      } else {
        console.error('Price calculation failed:', result.message);
        setPriceCalculation(null);
      }
    } catch (error) {
      console.error('Price calculation error:', error);
      setPriceCalculation(null);
    } finally {
      setIsCalculatingPrice(false);
    }
  };

  const addToCart = (vehicle: Vehicle, quantity: number) => {
    dispatch({ type: 'ADD_TO_CART', payload: { vehicle, quantity } });
  };

  const addProductToCart = (product: Product, quantity: number) => {
    // Convert Product to Vehicle for cart compatibility
    let name: string;

    if (product.product_type === 'other') {
      // For other products (typ_id 19): use prod_title as description
      name = `${product.prod_ref} - ${product.prod_title}`;
    } else {
      // For mats: use pattern and material info
      const patternPart = product.pattern_exceptions
        ? `${product.pattern_title} | ${product.pattern_exceptions}`
        : product.pattern_title;
      name = `${product.prod_ref} - ${patternPart} | ${product.material_title}`;
    }

    const vehicle: Vehicle = {
      id: product.id,
      name: name,
      brand: product.make.toLowerCase() === 'dacia' ? 'dacia' : 'renault',
      price: product.prod_price
    };
    dispatch({ type: 'ADD_TO_CART', payload: { vehicle, quantity } });
  };

  const updateQuantity = (vehicleId: string, quantity: number) => {
    dispatch({ type: 'UPDATE_QUANTITY', payload: { vehicleId, quantity } });
  };

  const removeFromCart = (vehicleId: string) => {
    dispatch({ type: 'REMOVE_FROM_CART', payload: { vehicleId } });
  };

  const clearCart = () => {
    dispatch({ type: 'CLEAR_CART' });
  };

  return (
    <CartContext.Provider value={{
      state,
      priceCalculation,
      isCalculatingPrice,
      addToCart,
      addProductToCart,
      updateQuantity,
      removeFromCart,
      clearCart,
      calculatePrices
    }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}